import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, roleBadgeClass } from "@/lib/helpers";
import { useBadges, ChampionBadges } from "@/components/Badges";
import { InstallApp } from "@/components/InstallApp";
import { BrandText } from "@/lib/brand";
import { LogOut, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";
import { navFor } from "@/lib/access";
import { ChangePasswordButton } from "@/components/ChangePasswordDialog";
import { SaleDeletedAlert } from "@/components/SaleDeletedAlert";
import { ThemeToggle } from "@/components/ThemeToggle";
import { OpeningPrompt } from "@/components/opening/OpeningPrompt";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = navFor(user);
  const badges = useBadges();

  const initials = (user?.full_name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const onLogout = () => { logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 grain">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 glass border-b border-slate-800 flex items-center justify-between px-4 py-3">
        <Link to="/" className="text-sm text-white whitespace-nowrap min-w-0 truncate"><BrandText testId="brand-text-mobile" /></Link>
        <div className="flex items-center gap-2">
          <ThemeToggle testId="theme-mode-toggle-mobile" />
          <NotificationBell />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} className="p-2 rounded-lg hover:bg-slate-800">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={() => setOpen(false)} data-testid="mobile-menu-backdrop" />}
        <aside
          className={`fixed right-0 top-0 z-50 h-[100dvh] w-[86vw] max-w-xs border-l border-slate-800 bg-slate-900 transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}
            lg:sticky lg:left-0 lg:right-auto lg:z-30 lg:h-screen lg:w-72 lg:max-w-none lg:shrink-0 lg:translate-x-0 lg:border-l-0 lg:border-r lg:bg-slate-900/80 lg:backdrop-blur-xl`}
          data-testid="app-sidebar"
        >
          <div className="h-full flex flex-col overflow-y-auto overscroll-contain lg:overflow-hidden">
            <div className="px-6 py-5 lg:py-6 border-b border-slate-800 flex items-start justify-between gap-2">
              <div>
                <Link to="/" onClick={() => setOpen(false)} className="block text-white"><BrandText className="text-[16px] whitespace-nowrap !tracking-[0.1em]" /></Link>
                <p className="text-xs text-slate-400 mt-1 font-mono">Satış Hedef Platformu</p>
              </div>
              <button onClick={() => setOpen(false)} className="lg:hidden p-2 -mr-2 rounded-lg hover:bg-slate-800" data-testid="mobile-menu-close"><X size={20} /></button>
            </div>

            <nav className="lg:flex-1 px-3 py-4 space-y-1 lg:overflow-y-auto">
              {items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/" || to === "/sales"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? "bg-blue-600/20 text-blue-200 border border-blue-500/30 shadow-[0_4px_20px_rgba(37,99,235,0.15)]"
                        : "text-slate-300 hover:bg-slate-800/60 hover:text-white border border-transparent"
                    }`
                  }
                >
                  <Icon size={18} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>

            <div className="px-4 py-4 border-t border-slate-800 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-10 w-10 border border-slate-700">
                  <AvatarFallback className="bg-slate-800 text-blue-300 font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <Link to="/profile" onClick={() => setOpen(false)} className="block text-sm font-semibold truncate hover:text-yellow-200" data-testid="sidebar-user-name">{user?.full_name}</Link>
                  <div className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border ${roleBadgeClass(user?.custom_role_id ? "custom" : user?.role)}`} data-testid="sidebar-user-role">
                    {roleLabel(user)}
                  </div>
                </div>
              </div>
              <div className="lg:hidden mb-3" data-testid="mobile-password-wrap"><ChangePasswordButton /></div>
              <InstallApp />
              <div className="mb-3 empty:hidden" data-testid="sidebar-badges"><ChampionBadges userId={user?.id} badges={badges} /></div>
              <Button
                data-testid="logout-btn"
                variant="outline"
                size="sm"
                className="w-full border-slate-700 bg-slate-800/50 hover:bg-slate-700"
                onClick={onLogout}
              >
                <LogOut size={14} className="mr-2" /> Çıkış
              </Button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 relative z-10">
          {/* Desktop top bar with notifications */}
          <div className="hidden lg:flex sticky top-0 z-20 glass border-b border-slate-800 px-6 py-3 justify-end items-center gap-3">
            <ThemeToggle />
            <ChangePasswordButton />
            <NotificationBell />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
            {children}
          </div>
        </main>
      </div>
      <SaleDeletedAlert />
      <OpeningPrompt />
    </div>
  );
}
